import { useState, useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChefHat, Download } from 'lucide-react'
import { useInstallPrompt } from './InstallAppButton'

export const DARK = '#1E2D27'
export const TEAL = '#5BB8A0'
export const BG = '#F0FAF7'

export function MarketingNavBar() {
  const [scrolled, setScrolled] = useState(false)
  const { canInstall, promptInstall } = useInstallPrompt()

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-200"
      style={{
        background: scrolled ? 'rgba(30,45,39,0.97)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(91,184,160,0.15)' : 'none',
      }}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: TEAL }}>
            <ChefHat className="w-4.5 h-4.5 text-white" size={18} />
          </div>
          <span className="font-bold text-white text-lg">CukrászDoc</span>
        </Link>
        <div className="flex items-center gap-3">
          {canInstall && (
            <button onClick={promptInstall}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-2.5 sm:px-3 py-2 rounded-lg transition-all"
              style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
              <Download size={15} />
              <span className="hidden sm:inline">Alkalmazás telepítése</span>
              <span className="sm:hidden">Telepítés</span>
            </button>
          )}
          <Link to="/kapcsolat"
            className="hidden sm:inline text-sm font-medium transition-colors"
            style={{ color: 'rgba(255,255,255,0.7)' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}>
            Kapcsolat
          </Link>
          <Link to="/login"
            className="text-sm font-medium transition-colors"
            style={{ color: 'rgba(255,255,255,0.7)' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}>
            Bejelentkezés
          </Link>
          <Link to="/register"
            className="text-sm font-semibold px-4 py-2 rounded-lg transition-all"
            style={{ background: TEAL, color: '#fff' }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
            Próbáld ki ingyen
          </Link>
        </div>
      </div>
    </nav>
  )
}

export function MarketingFooter() {
  return (
    <footer style={{ background: '#111a16' }} className="py-10">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: TEAL }}>
              <ChefHat size={14} className="text-white" />
            </div>
            <span className="font-bold text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>CukrászDoc</span>
          </Link>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
            © {new Date().getFullYear()} CukrászDoc — Minden jog fenntartva
          </p>
          <div className="flex gap-5">
            <Link to="/rolunk" className="text-xs transition-colors hover:underline" style={{ color: 'rgba(255,255,255,0.3)' }}>Rólunk</Link>
            <Link to="/kapcsolat" className="text-xs transition-colors hover:underline" style={{ color: 'rgba(255,255,255,0.3)' }}>Kapcsolat</Link>
            <Link to="/aszf" className="text-xs transition-colors hover:underline" style={{ color: 'rgba(255,255,255,0.3)' }}>ÁSZF</Link>
            <Link to="/aszf" className="text-xs transition-colors hover:underline" style={{ color: 'rgba(255,255,255,0.3)' }}>Adatkezelés</Link>
          </div>
        </div>
        <p className="text-center sm:text-left text-xs mt-2" style={{ color: 'rgba(255,255,255,0.25)' }}>
          <a href="mailto:info@cukraszdoc.hu" className="hover:underline">info@cukraszdoc.hu</a>
        </p>
        <div className="flex flex-wrap justify-center sm:justify-start gap-x-6 gap-y-2 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {[
            { to: '/gyartmanylap', label: 'Gyártmánylap készítés' },
            { to: '/haccp-igazolas', label: 'HACCP igazolás' },
            { to: '/szarmazasi-igazolas', label: 'Származási igazolás' },
            { to: '/receptbol-kaloria-szamitas', label: 'Receptből kalória számítás' },
            { to: '/allergen-cimke-nyomtatas', label: 'Allergén jelölés és címke' },
          ].map(({ to, label }) => (
            <Link key={to} to={to} className="text-xs transition-colors hover:underline" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  )
}

export function MarketingSection({
  id, bg, className, children,
}: { id?: string; bg?: string; className?: string; children: ReactNode }) {
  return (
    <section id={id} style={bg ? { background: bg } : undefined} className={className}>
      {children}
    </section>
  )
}

export function FaqAccordion({ items }: { items: { q: string; a: string }[] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  return (
    <div className="flex flex-col gap-3">
      {items.map(({ q, a }, i) => (
        <div key={q}
          className="rounded-2xl overflow-hidden cursor-pointer"
          style={{ border: '1px solid rgba(91,184,160,0.2)', background: '#fff' }}
          onClick={() => setOpenIdx(o => (o === i ? null : i))}
        >
          <div className="flex items-center justify-between px-6 py-5 gap-4">
            <p className="font-semibold text-sm" style={{ color: DARK }}>{q}</p>
            <span style={{ color: TEAL, flexShrink: 0, transform: openIdx === i ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▾</span>
          </div>
          {openIdx === i && (
            <div className="px-6 pb-5">
              <p className="text-sm leading-relaxed text-gray-500">{a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export function faqJsonLd(items: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  }
}

export function KeywordCrossLinks({ current }: { current: string }) {
  const links = [
    { to: '/gyartmanylap', label: 'Gyártmánylap készítés' },
    { to: '/haccp-igazolas', label: 'HACCP igazolás' },
    { to: '/szarmazasi-igazolas', label: 'Származási igazolás' },
    { to: '/receptbol-kaloria-szamitas', label: 'Receptből kalória számítás' },
    { to: '/allergen-cimke-nyomtatas', label: 'Allergén jelölés és címke' },
    { to: '/karacsonyi-vasar-elelmiszer-arusitas', label: 'Karácsonyi vásári árusítás' },
  ].filter(l => l.to !== current)

  return (
    <div className="max-w-6xl mx-auto px-6 py-14">
      <p className="text-sm font-semibold mb-4" style={{ color: DARK }}>Ez is érdekelhet</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {links.map(({ to, label }) => (
          <Link key={to} to={to}
            className="rounded-xl px-5 py-4 text-sm font-medium transition-all hover:-translate-y-0.5"
            style={{ background: BG, border: '1px solid rgba(91,184,160,0.2)', color: DARK }}>
            {label} →
          </Link>
        ))}
      </div>
    </div>
  )
}
